import React, { useContext } from 'react';
import { useNavigate } from 'react-router';
import Button from '@ingka/button';
import chevronLeft from '@ingka/ssr-icon/paths/chevron-left';
import closeIcon from '@ingka/ssr-icon/paths/cross';
import { styled } from 'styled-components';
import { useTranslation } from 'react-i18next';
import Progress from './Progress';
import { useStateSelector } from '../store/';
import {
  addPersistingQueryParamsToPath,
  getPrevPath,
} from '../helpers/urlHelper';
import { type EventName, sendEventToParent } from '../eventPublisher';
import colors from '../styles/colors';
import { selectAppOperation } from '../reducers/selectors';
import { getActiveStep } from '../helpers/progressHelper';
import type { OperationPage } from '../types/app';
import { ViewContext } from '../context';

interface StickyHeaderProps {
  page?: OperationPage;
  children?: React.ReactNode;
  isLoading?: boolean;
  customEventName?: EventName;
}

export const HEADER_HEIGHT = '72px';

const HeaderContainer = styled.div`
  background-color: ${colors.neutral.white};
  padding: 1rem;
  position: sticky;
  top: 0;
  z-index: 300;
  height: ${HEADER_HEIGHT};
`;

const HeaderControllers = styled.div<{ $multiple: boolean }>`
  display: flex;
  justify-content: ${(props) =>
    props.$multiple ? 'space-between' : 'flex-end'};
`;

const StickyHeader: React.FC<StickyHeaderProps> = ({
  page,
  children,
  isLoading,
  customEventName = 'BOKA/BOKA_CLOSED',
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const view = useContext(ViewContext);
  const operation = useStateSelector(selectAppOperation);
  const disabledPrevButton = useStateSelector(
    (state) => state.app.disabledPrevButton,
  );
  const showStep = page && page !== 'confirm';
  const step = showStep && getActiveStep(disabledPrevButton, page);

  return (
    <HeaderContainer>
      <HeaderControllers $multiple={!!step && step > 1}>
        {step && step > 1 && (
          <Button
            type="tertiary"
            iconOnly
            onClick={() =>
              navigate(
                addPersistingQueryParamsToPath(getPrevPath(operation, view)),
              )
            }
            size="small"
            ssrIcon={chevronLeft}
            disabled={isLoading}
            aria-label={t('components.stickyHeader.navigateBack')}
            data-cy="navigate-back-in-sticky-header"
          />
        )}
        {showStep && <Progress page={page} />}
        <Button
          type="tertiary"
          iconOnly
          onClick={() => sendEventToParent(customEventName, view)}
          size="small"
          ssrIcon={closeIcon}
          disabled={isLoading}
          aria-label={t('components.stickyHeader.closeModal')}
          data-cy="exit-button-in-sticky-header"
        />
      </HeaderControllers>
      {children}
    </HeaderContainer>
  );
};

export default StickyHeader;
